import {
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled, type SxProps, type Theme } from '@mui/material/styles';
import Select, {
  type SelectOption,
} from '@/components/@IntranetLibrary/Select';
import SearchInput from '@/components/@IntranetLibrary/SearchInput';
import { Body2 } from '@/components/@IntranetLibrary/Typography';

export type SearchableSelectOption = SelectOption & {
  /** 같은 값끼리 묶어 머리글(고를 수 없음)을 붙임. 옵션은 묶음 순서대로 넘길 것 */
  group?: string;
};

type BaseProps = {
  options: SearchableSelectOption[];
  placeholder?: ReactNode;
  disabled?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
  searchPlaceholder?: string;
  /** 옵션이 하나도 없을 때 메뉴 안내 */
  noOptionsText?: ReactNode;
  /** 검색에 걸린 옵션이 없을 때 메뉴 안내. 기본 '검색 결과가 없습니다.' */
  noResultsText?: ReactNode;
  /** 기본은 label 대소문자 무시 포함 검색(레거시 kendo filter: "contains") */
  filter?: (option: SearchableSelectOption, query: string) => boolean;
};

type SingleProps = BaseProps & {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
  renderValue?: (value: string) => ReactNode;
};

type MultipleProps = BaseProps & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
  renderValue?: (value: string[]) => ReactNode;
};

export type SearchableSelectProps = SingleProps | MultipleProps;

const GROUP_PREFIX = '__searchable-select-group__:';

const containsLabel = (option: SearchableSelectOption, query: string) =>
  option.label.toLowerCase().includes(query.trim().toLowerCase());

/**
 * 공통 Select + 메뉴 안 공통 SearchInput(레거시 kendo 콤보·다중 선택의 contains 필터 대응).
 * MUI Select 는 메뉴 자식 클릭을 선택+닫기로 덮어쓰고 키 입력을 type-ahead 로 가져가므로 검색 줄에서 둘 다 끊음.
 * Tab 은 메뉴를 닫음 — 안 닫으면 포커스만 메뉴 밖으로 빠져 본문이 스크롤될 때 메뉴가 셀렉트에서 떨어져 남음
 * 하나 고르기는 고른 옵션이 검색에 안 걸려도 목록에서 빼지 않고 CSS 로만 숨김 — 빼면 MUI out-of-range 경고
 */
const SearchableSelect = (props: SearchableSelectProps) => {
  const {
    options,
    placeholder,
    disabled,
    className,
    sx,
    searchPlaceholder,
    noOptionsText,
    noResultsText,
    filter = containsLabel,
  } = props;
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const close = () => {
    setOpen(false);
    // 다시 열면 전체 목록
    setQuery('');
  };

  const selected = useMemo(
    () => new Set(props.multiple ? props.value : [props.value]),
    [props.multiple, props.value],
  );

  // 하나 고르기에서 검색에 안 걸린 고른 옵션 — 목록엔 두고 눈에만 안 보이게
  const maskedValue = useMemo(() => {
    const keyword = query.trim();
    if (props.multiple || !keyword || !props.value) return null;
    const option = options.find((o) => o.value === props.value);
    return option && !option.isHidden && !filter(option, keyword)
      ? option.value
      : null;
  }, [props.multiple, props.value, options, query, filter]);

  const menuOptions = useMemo<SelectOption[]>(() => {
    const keyword = query.trim();
    const isVisible = (o: SearchableSelectOption) =>
      !o.isHidden && (!keyword || filter(o, keyword));
    const result: SelectOption[] = [];
    options.forEach((o, i) => {
      if (o.group !== undefined && options[i - 1]?.group !== o.group) {
        const group = o.group;
        result.push({
          value: `${GROUP_PREFIX}${group}`,
          label: group,
          disabled: true,
          isHidden: !options.some((x) => x.group === group && isVisible(x)),
        });
      }
      result.push({
        value: o.value,
        label: o.label,
        labelNode: o.labelNode,
        disabled: o.disabled,
        isHidden: o.value !== maskedValue && !isVisible(o),
      });
    });
    return result;
  }, [options, query, filter, maskedValue]);

  const hasVisibleOption = menuOptions.some(
    (o) =>
      !o.isHidden &&
      o.value !== maskedValue &&
      !o.value.startsWith(GROUP_PREFIX),
  );

  const stopMenuKeyHandling = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'Tab') return;
    e.stopPropagation();
  };
  const stopMenuClick = (e: MouseEvent<HTMLDivElement>) => e.stopPropagation();

  let emptyText: ReactNode = null;
  if (!options.length) emptyText = noOptionsText ?? null;
  else if (!hasVisibleOption)
    emptyText = noResultsText ?? t('검색 결과가 없습니다.');

  return (
    <Select
      className={className}
      sx={sx}
      multiple={props.multiple}
      value={props.value}
      options={menuOptions}
      placeholder={placeholder}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={close}
      MenuProps={{
        autoFocus: false,
        onKeyDown: (e) => {
          if (e.key !== 'Tab') return;
          e.preventDefault();
          close();
        },
        ...(maskedValue !== null && {
          sx: {
            [`& [data-value="${maskedValue.replace(/"/g, '\\"')}"]`]: {
              display: 'none',
            },
          },
        }),
      }}
      // 비었을 때는 공통 Select 의 placeholder 표시를 그대로 씀
      renderValue={
        props.renderValue && selected.size > 0 && !selected.has('')
          ? (value) =>
              props.multiple
                ? props.renderValue?.(value as string[])
                : props.renderValue?.(value as string)
          : undefined
      }
      onChange={(e) => {
        const value = e.target.value;
        if (props.multiple)
          props.onChange(
            (Array.isArray(value) ? value : String(value).split(',')).filter(
              (v) => !v.startsWith(GROUP_PREFIX),
            ),
          );
        else props.onChange(String(value ?? ''));
      }}
      infoTextNode={
        <MenuFilterRow>
          <Box onClick={stopMenuClick} onKeyDown={stopMenuKeyHandling}>
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder ?? t('검색')}
              aria-label={searchPlaceholder ?? t('검색')}
            />
            {emptyText && (
              <Body2 component="div" sx={{ p: '8px 6px' }}>
                {emptyText}
              </Body2>
            )}
          </Box>
        </MenuFilterRow>
      }
    />
  );
};

/** 공통 Select 메뉴 목록(SelectMenuPaperSx 의 .MuiList-root) 안쪽 여백 — sticky 가 그만큼 내려와 멈춰 틈으로 항목이 비침 */
const MENU_LIST_PADDING = 10;

const MenuFilterRow = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: -MENU_LIST_PADDING,
  marginTop: -MENU_LIST_PADDING,
  paddingTop: MENU_LIST_PADDING,
  zIndex: 1,
  paddingBottom: theme.spacing(1),
  backgroundColor: 'var(--dk-bg, #fff)',
}));

export default SearchableSelect;
